// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { DataTableBody, DataTableCell, DataTableRow } from "@insite/mobius/index";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import DataTable from "@rpsc/MobiusOverrides/DataTable/DataTable";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsAttributesStyles {
    wrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    // XNG-Change
    table?: InjectableCss;
    tableContainer?: InjectableCss;
    label?: TypographyPresentationProps;
    value?: TypographyPresentationProps;
    valueCell?: InjectableCss;
    labelCell?: InjectableCss;
}

export const attributesStyles: ProductDetailsAttributesStyles = {
    wrapper: {
        css: css`
            padding-bottom: 15px;
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },
    attributeTypeValuesText: {
        css: css`
            margin-bottom: 5px;
        `,
    },
};

const styles = attributesStyles;
type Row = { key: string; value: any };

const ProductDetailsAttributes: React.FC<Props> = ({ product: { brand, attributeTypes } }) => {
    if (!brand && (!attributeTypes || attributeTypes.length === 0)) {
        return null;
    }

    // XNG-Change
    const mappedAttributes = (attributeTypes || [])
        .filter(attributeType => attributeType.includeOnProduct)
        .map(({ label, attributeValues }) => ({
            key: label,
            value: attributeValues?.map(({ valueDisplay }) => valueDisplay)?.join(", ") || "",
        }));

    let attributeTableRows1: Row[] = [];
    let attributeTableRows2: Row[] = [];

    const halfAttributes = Math.ceil(mappedAttributes.length / 2);
    attributeTableRows1 = mappedAttributes.slice(0, halfAttributes);
    attributeTableRows2 = mappedAttributes.slice(halfAttributes);

    // XNG-ChangeRemove brand and show 2 tables
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_attributes">
            {/* {brand && (
                <Typography data-test-selector="brand_item" as="p" {...styles.attributeTypeValuesText}>
                    <Typography data-test-selector="brand_item_label" {...styles.attributeTypeLabelText}>
                        {translate("Brand")}&nbsp;:&nbsp;
                    </Typography>
                    <span data-test-selector="brand_item_value">{brand.name}</span>
                </Typography>
            )} */}
            {/* {limitedAttributeTypes.map(attributeType => (
                <Typography
                    key={attributeType.id.toString()}
                    data-test-selector="attributes_item"
                    data-attributetypeid={attributeType.id}
                    {...styles.attributeTypeValuesText}
                    as="p"
                >
                    <Typography data-test-selector="attributes_item_label" {...styles.attributeTypeLabelText}>
                        {attributeType.label || attributeType.name}&nbsp;:&nbsp;
                    </Typography>
                    <span data-test-selector="attributes_item_value">
                        {(attributeType.attributeValues ?? [])
                            .map(attributeValue => attributeValue.valueDisplay)
                            .join(", ")}
                    </span>
                </Typography>
            ))} */}
            {!!attributeTableRows1.length && <KeyValueTableSection rows={attributeTableRows1} />}
            {!!attributeTableRows2.length && <KeyValueTableSection rows={attributeTableRows2} />}
        </StyledWrapper>
    );
};

const KeyValueTableSection = ({ rows }: { rows: Row[] }) => {
    return (
        <StyledWrapper {...styles.tableContainer}>
            <DataTable {...styles.table}>
                <DataTableBody>
                    {rows.map(({ key, value }) => (
                        <DataTableRow key={key}>
                            <DataTableCell {...styles.labelCell}>
                                <Typography {...styles.label}>{key}</Typography>
                            </DataTableCell>
                            <DataTableCell {...styles.valueCell}>
                                <Typography {...styles.value}>{value}</Typography>
                            </DataTableCell>
                        </DataTableRow>
                    ))}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsAttributes),
    definition: {
        displayName: "Attributes",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
